import i18next, { Resource } from 'i18next';
import { initReactI18next } from 'react-i18next';

type LocaleModule = { default: Record<string, unknown> };

const LANGUAGE_STORAGE_KEY = 'presence-admin-locale';

const localeModules = import.meta.glob<LocaleModule>('./messages/*.json', {
  eager: true,
});

const resources: Resource = {};
const localeOptions: Array<{ value: string; label: string }> = [];

for (const [path, module] of Object.entries(localeModules)) {
  const match = path.match(/\/([^/]+)\.json$/);
  if (!match) {
    continue;
  }
  const locale = match[1];
  const translation = module.default ?? {};
  resources[locale] = { translation };
  let label = locale;
  const meta =
    typeof translation === 'object' && translation !== null
      ? (translation.meta as Record<string, unknown> | undefined)
      : undefined;
  if (meta && typeof meta.languageName === 'string') {
    label = meta.languageName;
  }
  localeOptions.push({ value: locale, label });
}

localeOptions.sort((a, b) => a.label.localeCompare(b.label));

const SUPPORTED_LOCALES = localeOptions.map((option) => option.value);

const FALLBACK_LOCALE = SUPPORTED_LOCALES.includes('pt-BR')
  ? 'pt-BR'
  : SUPPORTED_LOCALES[0] ?? 'en-US';

function resolveInitialLocale(): string {
  if (typeof window === 'undefined') {
    return FALLBACK_LOCALE;
  }
  const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  if (stored && SUPPORTED_LOCALES.includes(stored)) {
    return stored;
  }
  const navigatorLocale = window.navigator?.language;
  if (navigatorLocale) {
    const exactMatch = SUPPORTED_LOCALES.find(
      (locale) => locale.toLowerCase() === navigatorLocale.toLowerCase()
    );
    if (exactMatch) {
      return exactMatch;
    }
    const languageOnly = navigatorLocale.split('-')[0]?.toLowerCase();
    if (languageOnly) {
      const partial = SUPPORTED_LOCALES.find((locale) => locale.toLowerCase().startsWith(languageOnly));
      if (partial) {
        return partial;
      }
    }
  }
  return FALLBACK_LOCALE;
}

i18next.use(initReactI18next).init({
  resources,
  lng: resolveInitialLocale(),
  fallbackLng: FALLBACK_LOCALE,
  interpolation: {
    escapeValue: false,
  },
  defaultNS: 'translation',
});

function setLocale(locale: string) {
  if (!SUPPORTED_LOCALES.includes(locale)) {
    return;
  }
  i18next.changeLanguage(locale).catch((error) => {
    console.warn('Failed to change admin locale', error);
  });
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, locale);
  }
}

export { i18next as i18n, localeOptions as LOCALE_OPTIONS, SUPPORTED_LOCALES, FALLBACK_LOCALE as DEFAULT_LOCALE, LANGUAGE_STORAGE_KEY, setLocale };
